'use client'

import { useMemo, useState } from 'react'
import { type CategoryWithChildren } from './use-category-by-parent'
import { Button, type ButtonProps } from '@mui/material'
import { Icon } from '@/icons/icon'
import { type Category } from '@/lib/generated/graphql'
import Link from 'next/link'
import { getCategoryName } from '@/utils/functions/category/utils/get-category-name'
import { routeCalculatorRelative } from '@/lib/route-calculator'
import { ResolvedImage } from '../utility/image'

const COLLAPSED_CATEGORIES_PER_SECTION = 4
const MAX_SECTIONS_TO_AUTOEXPAND = 5

interface Collapsed {
  collapsedCategories: CategoryWithChildren[]
  hasMore: boolean
}

// We have a whole category, generate a link to it
interface CategoryLinkProps {
  category: Category
}

const getName = (category: Category) =>
  getCategoryName(category.category_categorylevel, category, true)

const ParentCategoryLink = ({ category }: CategoryLinkProps) => (
  <Link
    prefetch={false}
    href={routeCalculatorRelative.categoriesRoute(category)}
    className='flex items-center text-[21px] font-bold leading-5 underline'
  >
    <ResolvedImage
      src={category.category_iconUrl as string}
      alt=''
      className='mr-[6px] h-[20px]'
      height={20}
      width={20}
    />
    <h2>{getName(category)}</h2>
  </Link>
)

const ChildCategoryLink = ({ category }: CategoryLinkProps) => (
  <Link
    prefetch={false}
    href={routeCalculatorRelative.categoriesRoute(category)}
    className='text-sm no-underline'
  >
    {getName(category)}
  </Link>
)

const ExpandToggle = (props: ButtonProps) => {
  return (
    <Button
      {...props}
      style={{
        display: 'flex',
        alignItems: 'center',
        fontWeight: 'bold',
        marginLeft: 16,
      }}
    />
  )
}

const collapseTree = (categories: CategoryWithChildren[]): Collapsed => {
  const totalCount = categories
    .map(({ children }) => children.length + 1)
    .reduce((a, b) => a + b, 0)
  if (totalCount <= MAX_SECTIONS_TO_AUTOEXPAND) {
    return {
      collapsedCategories: categories,
      hasMore: false,
    }
  }

  const collapsedCategories: CategoryWithChildren[] = []
  let remaining = COLLAPSED_CATEGORIES_PER_SECTION

  for (const { category, children } of categories) {
    if (remaining > 0) {
      const childrenToAdd = Math.min(
        Math.max(0, remaining - 1),
        children.length,
      )
      const newCategory: CategoryWithChildren = {
        category,
        children: children.slice(0, childrenToAdd),
      }
      collapsedCategories.push(newCategory)
      remaining -= childrenToAdd + 1
    }
  }

  return {
    collapsedCategories,
    hasMore: true,
  }
}

export const CategorySectionInner = ({
  childCategories,
  parentCategory,
}: {
  childCategories: CategoryWithChildren[]
  parentCategory: Category
}) => {
  const { collapsedCategories, hasMore } = useMemo(
    () => collapseTree(childCategories),
    [childCategories],
  )
  const [isExpanded, setIsExpanded] = useState(!hasMore)
  const categoriesToShow = isExpanded ? childCategories : collapsedCategories

  return (
    <li className='mb-4 ml-0 mr-6 mt-2 w-full list-none p-0 sm:w-[45%] md:w-[30%] lg:w-[17%]'>
      <ParentCategoryLink category={parentCategory} />
      <ul className='ml-8 p-4'>
        {categoriesToShow.map(({ category, children }) => (
          <li
            className='m-0 mb-2 list-none p-0'
            key={category.category_categoryId}
          >
            <ChildCategoryLink category={category} />
            <ul className='mb-0 ml-4 mr-0 mt-1 p-0'>
              {children.map((leafCategory) => (
                <li
                  className='m-0 mb-2 list-none p-0'
                  key={leafCategory.category_categoryId}
                >
                  <ChildCategoryLink category={leafCategory} />
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      {hasMore &&
        (isExpanded ? (
          <ExpandToggle
            onClick={() => {
              setIsExpanded(false)
            }}
          >
            <div className='mr-1 text-sm capitalize text-dark-dark'>Fewer</div>
            <Icon name='arrow-up-1-regular' color='dark' size='xs' />
          </ExpandToggle>
        ) : (
          <ExpandToggle
            onClick={() => {
              setIsExpanded(true)
            }}
          >
            <div className='mr-1 text-sm capitalize text-dark-dark'>More</div>
            <Icon name='arrow-down-1-regular' color='dark' size='xs' />
          </ExpandToggle>
        ))}
    </li>
  )
}
